import type {Intent, IntentType} from '../intent';
import {mergeIntents} from '../intent';
import {$intent} from '../intent/constants';
import {isViewConfigIntent} from './ConfigIntent';
import type {
  createSubviewIntents,
  InternalSubviews,
} from './createSubviewIntents';
import type {View} from './types';
import {
  attachElementRef,
  getInstanceId,
  getSubviewOwner,
  recordSubviewOwners,
  InspectionCommit,
} from './inspectionRegistry';

/** Stable engine-local identity and display labels for the originating view. */
export interface IntentOwner {
  readonly instanceId: number;
  readonly view: string;
  readonly config: string;
}

export interface IntentSource {
  readonly type:
    | 'base'
    | 'css'
    | 'uses'
    | 'customize'
    | 'variant'
    | 'subview'
    | 'tokens';
  readonly owner: IntentOwner;
  readonly name?: string;
  readonly value?: string | number | boolean | null;
  readonly parent?: IntentSource;
}

/** One intent occurrence captured before the intent type merges its payload. */
export interface IntentRecord {
  readonly intent: Intent<unknown>;
  /** Undefined when the originating render did not record its source. */
  readonly source: IntentSource | undefined;
  readonly priority: View.Priority;
  /** True when this record was copied from an inherited revision. */
  readonly inherited?: boolean;
}

/**
 * Intent records for one render with EngineProvider debug enabled.
 * References are live; readers must use committed revisions.
 * See ./inspection.md for how records and sources are captured.
 */
export interface ViewInspection {
  readonly owner: IntentOwner;
  readonly records: IntentRecord[];
  /** False when expected records or source labels are missing. */
  complete: boolean;
  /** The owning subview slot, separate from this view's style inheritance. */
  readonly subview?: {
    readonly ownerInstanceId: number;
    readonly name: string;
  };
}

function createInspection<Props>(
  revision: View.ViewRevision<Props>,
  displayName: string,
): ViewInspection {
  return {
    owner: {
      instanceId: getInstanceId(revision),
      view: displayName,
      config: revision.instance.config.options.name ?? '',
    },
    records: [],
    complete: true,
    subview: getSubviewOwner(revision),
  };
}

function recordIntent<Props>(
  revision: View.ViewRevision<Props>,
  intent: Intent<unknown>,
  priority: View.Priority,
  source: IntentSource | undefined,
): void {
  const {inspection} = revision;
  if (!inspection) {
    return;
  }
  if (!source) {
    inspection.complete = false;
  }
  inspection.records.push({intent, source, priority});
}

function createSource<Props>(
  revision: View.ViewRevision<Props>,
  type: IntentSource['type'],
  name?: string,
): IntentSource | undefined {
  return revision.inspection
    ? {type, owner: revision.inspection.owner, name}
    : undefined;
}

function createSourceFromParent(
  parent: IntentSource | undefined,
  type: 'variant' | 'subview',
  name: string,
  value?: IntentSource['value'],
): IntentSource | undefined {
  return parent && {type, owner: parent.owner, parent, name, value};
}

/** Fail closed on missing/misaligned metadata; inspection must not break rendering. */
function validateSourceCount(
  inspection: ViewInspection,
  intents: readonly unknown[],
  sources: readonly (IntentSource | undefined)[] | undefined,
): readonly (IntentSource | undefined)[] {
  if (!intents.length) {
    return [];
  }
  if (sources?.length === intents.length) {
    return sources;
  }
  inspection.complete = false;
  return new Array<undefined>(intents.length).fill(undefined);
}

function isSourceArray(
  source: IntentSource | readonly (IntentSource | undefined)[] | undefined,
): source is readonly (IntentSource | undefined)[] {
  return Array.isArray(source);
}

function getSources(
  inspection: ViewInspection,
  intents: readonly unknown[],
  records: readonly Pick<IntentRecord, 'intent' | 'source'>[],
): readonly (IntentSource | undefined)[] {
  const aligned =
    intents.length === records.length &&
    intents.every((intent, index) => intent === records[index].intent);
  return validateSourceCount(
    inspection,
    intents,
    aligned ? records.map(({source}) => source) : undefined,
  );
}

/** Keep the originating view's sources when deferred config bindings resolve here. */
function getSourcesFromInheritedConfigIntents<Props>(
  revision: View.ViewRevision<Props>,
  inheritedRevision: View.ViewRevision<Props> | void,
  intents: readonly View.Intent<Props>[] | void,
): readonly (IntentSource | undefined)[] | undefined {
  const {inspection} = revision;
  if (!inspection || !intents) {
    return undefined;
  }

  const inheritedRecords = inheritedRevision?.inspection?.records ?? [];
  const configRecords = inheritedRecords.filter(({intent}) =>
    isViewConfigIntent(revision, intent),
  );
  return getSources(inspection, intents, configRecords);
}

// Forwarding the same intent array preserves its source lookup.
let subviewSourcesByIntents:
  | WeakMap<readonly Intent<unknown>[], readonly (IntentSource | undefined)[]>
  | undefined;

/** Retrieve the sources recorded by the view that populated the subview. */
function getSourcesFromInheritedSubviewIntents<Props>(
  revision: View.ViewRevision<Props>,
  intents: View.SubviewIntents<Props> | void,
): readonly (IntentSource | undefined)[] | undefined {
  const {inspection} = revision;
  if (!inspection || !intents) {
    return undefined;
  }

  return validateSourceCount(
    inspection,
    intents,
    subviewSourcesByIntents?.get(intents),
  );
}

/** Reconstruct sources by subview name, matching the runtime intent order. */
function createSourcesBySubviewName(
  inspection: ViewInspection,
  subviewIntentType: typeof createSubviewIntents,
  mergedSubviewIntents: InternalSubviews | undefined,
): Map<string, readonly (IntentSource | undefined)[]> {
  if (!mergedSubviewIntents) {
    return new Map();
  }
  const recordsBySubview = new Map<
    string,
    Pick<IntentRecord, 'intent' | 'source'>[]
  >();
  for (const {intent, source} of inspection.records) {
    if (subviewIntentType.isIntent(intent)) {
      for (const [name, intents] of Object.entries(intent)) {
        const entries = recordsBySubview.get(name) ?? [];
        const subviewSource = createSourceFromParent(source, 'subview', name);
        for (const child of intents) {
          entries.push({intent: child, source: subviewSource});
        }
        recordsBySubview.set(name, entries);
      }
    }
  }
  return new Map(
    Object.entries(mergedSubviewIntents).map(([name, intents]) => [
      name,
      getSources(inspection, intents, recordsBySubview.get(name) ?? []),
    ]),
  );
}

/** Store sources for the exact intent array forwarded to the receiving view. */
function recordSubviewSources<Props>(
  revision: View.ViewRevision<Props>,
  sourcesBySubviewName:
    | ReadonlyMap<string, readonly (IntentSource | undefined)[]>
    | undefined,
  name: string,
  intents: View.SubviewIntents<unknown>,
  forwardedIntents?: View.SubviewIntents<unknown>,
): void {
  const {inspection} = revision;
  if (!inspection || !sourcesBySubviewName) {
    return;
  }
  let sources = sourcesBySubviewName.get(name) ?? [];
  if (forwardedIntents) {
    sources = sources.concat(
      validateSourceCount(
        inspection,
        forwardedIntents,
        subviewSourcesByIntents?.get(forwardedIntents),
      ),
    );
  } else if (intents.length > sources.length) {
    // Raw subview prop intents were appended after the existing intents.
    const propIntentCount = intents.length - sources.length;
    const propSource = createSource(revision, 'subview', name);
    sources = sources.concat(
      new Array<IntentSource | undefined>(propIntentCount).fill(propSource),
    );
  }
  subviewSourcesByIntents ??= new WeakMap();
  subviewSourcesByIntents.set(
    intents,
    validateSourceCount(inspection, intents, sources),
  );
}

/** Preserve records only for the intent types accepted by the runtime merge. */
function copyInheritedRecords<Props>(
  targetRevision: View.ViewRevision<Props>,
  inheritedRevision: View.ViewRevision<Props>,
  mergedTypeSymbols: ReadonlySet<symbol>,
): void {
  const {inspection} = targetRevision;
  if (!inspection || !mergedTypeSymbols.size) {
    return;
  }
  if (!inheritedRevision.inspection?.complete) {
    inspection.complete = false;
  }
  for (const record of inheritedRevision.inspection?.records ?? []) {
    if (mergedTypeSymbols.has(record.intent[$intent])) {
      // Readonly records already marked inherited can be shared across revisions.
      inspection.records.push(
        record.inherited ? record : {...record, inherited: true},
      );
    }
  }
}

/** Merge inherited intent state and debug records. Requires debug enabled. */
function mergeRevisionIntents<Props>(
  targetRevision: View.ViewRevision<Props>,
  inheritedRevision: View.ViewRevision<Props>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  shouldMerge: (type: IntentType<any, any>) => boolean,
): void {
  const mergedTypeSymbols = new Set<symbol>();
  // Track accepted types during the merge so the predicate runs only once.
  mergeIntents(targetRevision, inheritedRevision, (type) => {
    const merge = shouldMerge(type);
    if (merge) {
      mergedTypeSymbols.add(type.symbol);
    }
    return merge;
  });
  copyInheritedRecords(targetRevision, inheritedRevision, mergedTypeSymbols);
}

interface VariantRange {
  start: number;
  end: number;
  name: string;
  value: IntentSource['value'];
}

// The dynamic resolver receives the same result array recorded here.
let variantRangesByIntents:
  | WeakMap<readonly unknown[], VariantRange[]>
  | undefined;

/** Remember which appended intents came from this variant. */
function recordVariantForIntents<Props>(
  revision: View.ViewRevision<Props> | void,
  intents: View.Intent<Props>[],
  startIndex: number,
  name: string,
  value: unknown,
): void {
  if (!revision?.inspection) {
    return;
  }

  variantRangesByIntents ??= new WeakMap();
  let ranges = variantRangesByIntents.get(intents);
  if (!ranges) {
    ranges = [];
    variantRangesByIntents.set(intents, ranges);
  }
  ranges.push({
    start: startIndex,
    end: intents.length,
    name,
    value:
      value === null ||
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
        ? value
        : undefined,
  });
}

/** Carry a dynamic intent's source into its results, refining it for variants. */
function createSourceFromDynamic<Props>(
  revision: View.ViewRevision<Props>,
  resolvedIntents: readonly unknown[] | void,
  source: IntentSource | undefined,
): IntentSource | readonly (IntentSource | undefined)[] | undefined {
  if (!revision.inspection) {
    return undefined;
  }

  const variantIntentRanges = resolvedIntents
    ? variantRangesByIntents?.get(resolvedIntents)
    : undefined;
  if (!resolvedIntents || !variantIntentRanges) {
    return source;
  }
  const sources = new Array<IntentSource | undefined>(
    resolvedIntents.length,
  ).fill(source);
  for (const {start, end, name, value} of variantIntentRanges) {
    sources.fill(
      createSourceFromParent(source, 'variant', name, value),
      start,
      end,
    );
  }
  return sources;
}

/** Select a source by its original intent-array index, checking counts once. */
function createSourceLookup<Props>(
  revision: View.ViewRevision<Props>,
  intents: readonly unknown[],
  source: IntentSource | readonly (IntentSource | undefined)[] | undefined,
): ((index: number) => IntentSource | undefined) | undefined {
  const {inspection} = revision;
  if (!inspection) {
    return undefined;
  }
  if (!source) {
    return undefined;
  }
  if (isSourceArray(source)) {
    const sources = validateSourceCount(inspection, intents, source);
    return (index) => sources[index];
  }
  return () => source;
}

export const debug = {
  InspectionCommit,
  attachElementRef,
  createInspection,
  createSource,
  createSourceLookup,
  createSourcesBySubviewName,
  createSourceFromDynamic,
  getSourcesFromInheritedConfigIntents,
  getSourcesFromInheritedSubviewIntents,
  recordIntent,
  recordVariantForIntents,
  recordSubviewSources,
  recordSubviewOwners,
  mergeRevisionIntents,
} as const;
